import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Loader2, Save, Edit, X } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { MembershipTierConfig } from '../../types'
import { useToast } from '../../hooks/use-toast'

export function AdminPlanManagement() {
  const [tiers, setTiers] = useState<MembershipTierConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [editingTier, setEditingTier] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Partial<MembershipTierConfig>>({})
  const [saving, setSaving] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    loadTiers()
  }, [])

  const loadTiers = async () => {
    try {
      setLoading(true)
      const data = await apiClient.membershipConfig.getAllTiers()
      setTiers(data)
    } catch (error) {
      console.error('Failed to load membership tiers:', error)
      toast({
        title: 'Error',
        description: 'Failed to load membership tiers',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const startEditing = (tier: MembershipTierConfig) => {
    setEditingTier(tier.tier_id)
    setEditForm({
      name: tier.name,
      price_monthly_usd: tier.price_monthly_usd,
      free_downloads_per_month: tier.free_downloads_per_month,
      translation_discount_percent: tier.translation_discount_percent,
      description: tier.description,
    })
  }

  const cancelEditing = () => {
    setEditingTier(null)
    setEditForm({})
  }

  const saveTier = async (tierId: string) => {
    try {
      setSaving(true)
      const updatedTier = await apiClient.membershipConfig.updateTier(tierId, {
        name: editForm.name!,
        price_monthly_usd: editForm.price_monthly_usd!,
        free_downloads_per_month: editForm.free_downloads_per_month!,
        translation_discount_percent: editForm.translation_discount_percent!,
        description: editForm.description!,
      })
      
      // Update local state
      setTiers(tiers.map(t => t.tier_id === tierId ? updatedTier : t))
      setEditingTier(null)
      setEditForm({})
      
      toast({
        title: 'Success',
        description: 'Membership tier updated successfully',
      })
    } catch (error: any) {
      console.error('Failed to update tier:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to update membership tier',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="plan-management flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="plan-management space-y-6">
      <div className="plan-header">
        <h2 className="text-xl font-semibold text-foreground">Membership Plans</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure pricing, benefits, and limits for each membership tier
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tiers.map((tier) => {
          const isEditing = editingTier === tier.tier_id
          
          return (
            <Card key={tier.tier_id} className={`tier-card ${isEditing ? 'ring-2 ring-primary' : ''}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    {isEditing ? (
                      <Input
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="font-bold text-lg mb-2"
                      />
                    ) : (
                      <CardTitle>{tier.name}</CardTitle>
                    )}
                    {isEditing ? (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-sm text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={editForm.price_monthly_usd ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, price_monthly_usd: parseFloat(e.target.value) || 0 })}
                          className="w-24"
                        />
                        <span className="text-sm text-muted-foreground">/month</span>
                      </div>
                    ) : (
                      <CardDescription>
                        {tier.price_monthly_usd === 0 ? 'Free' : `$${tier.price_monthly_usd}/month`}
                      </CardDescription>
                    )}
                  </div>
                  {!isEditing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEditing(tier)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <>
                    <div className="edit-field space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={editForm.description || ''}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        rows={2}
                      />
                    </div>
                    
                    <div className="edit-field space-y-2">
                      <Label>Free Downloads per Month</Label>
                      <Input
                        type="number"
                        value={editForm.free_downloads_per_month ?? ''}
                        onChange={(e) => setEditForm({ ...editForm, free_downloads_per_month: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    
                    <div className="edit-field space-y-2">
                      <Label>Translation Discount (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={editForm.translation_discount_percent ?? ''}
                        onChange={(e) => setEditForm({ ...editForm, translation_discount_percent: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    
                    <div className="edit-actions flex gap-2 mt-4">
                      <Button
                        onClick={() => saveTier(tier.tier_id)}
                        disabled={saving}
                        className="flex-1"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="mr-2 h-4 w-4" />
                            Save
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={cancelEditing}
                        disabled={saving}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">{tier.description}</p>
                    
                    <div className="benefits-list space-y-2 text-sm">
                      <div className="benefit-item flex justify-between">
                        <span className="text-muted-foreground">Free Downloads:</span>
                        <span className="font-medium">{tier.free_downloads_per_month}</span>
                      </div>
                      <div className="benefit-item flex justify-between">
                        <span className="text-muted-foreground">Translation Discount:</span>
                        <span className="font-medium">{tier.translation_discount_percent}%</span>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      <Card className="mt-6 bg-muted/50">
        <CardHeader>
          <CardTitle className="text-base">Important Notes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>• Changes to membership tiers take effect immediately for new subscriptions</p>
          <p>• Existing user memberships retain their current benefits until the next billing cycle</p>
          <p>• Free downloads reset monthly on each user's billing cycle start date</p>
          <p>• Translation discount applies to pay-as-you-go translation charges</p>
          <p>• Note: In production, tier configurations should be stored in a database for persistence</p>
        </CardContent>
      </Card>
    </div>
  )
}

